import { ComponentIds, Entity, SelectedComponent } from '@inter-ikea-core/core';
import {
  CompartmentComponent,
  Product,
  ProductType,
  SelectionOverrideComponent
} from '@inter-ikea-storageone/range';
import SelectInSceneInfo from 'components/SelectInSceneInfo/SelectInSceneInfo';
import { useCore } from 'contexts/core';
import { getMarketProducts } from 'ducks/products';
import { setShowSelectionOverrideIndicators } from 'ducks/selectionOverride';
import { CheckWidget } from 'rangeConfig/shared/widgets/checkWidget';
import FrontLayoutsSwiper from 'rangeConfig/shared/widgets/frontLayouts/FrontLayoutsSwiper';
import SingleColumnProductList from 'rangeConfig/shared/widgets/frontLayouts/SingleColumnProductList';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isMobile } from 'utils/platform';
import SelectInSceneImage from './Images/FrontLayoutSelectInScene.png';

import { NoneOption } from 'rangeConfig/@types';
import {
  addFrontLayoutToFrame,
  getFrontsInScene,
  getFrontsOnFrame,
  getMajorityDesign
} from 'rangeConfig/shared/utils/frontLayouts';

import { useAppDispatch } from 'ducks/hooks';
import { sortProducts } from 'products/utils';
import styled from 'styled-components';
import CompartmentModelComponent from '@inter-ikea-storageone/range/src/core-api/components/CompartmentModelComponent';
import { useBehaviorTracker } from 'contexts/behaviors';

export const List = styled.ul`
  margin: 0;
  padding: 0;
`;

export type Props = {
  onLayoutSelected: (layout: Product) => void;
  selectedLayout: string;
  suggestionsForSelectedFrame: Product[];
  noneOption?: NoneOption;
};

export default function FrontLayouts({ selected }: CheckWidget) {
  const coreApi = useCore();
  const products = useSelector(getMarketProducts);
  const entityManager = coreApi.getServices().entityManager;
  const dispatch = useAppDispatch();
  const behaviorTracker = useBehaviorTracker();
  const [selectedCompartment, setSelectedCompartment] = useState<Entity | null>(null);
  const [selectedLayout, setSelectedLayout] = useState<string>('');

  let suggestionsForSelectedFrame: Product[] = [];

  const frontLayoutProducts = useMemo(() => {
    return products.filter(p => p.type === ProductType.BESTA_FRONT_LAYOUT);
  }, [products]);

  if (selectedCompartment) {
    const compartmentSize = selectedCompartment.getComponent<CompartmentComponent>(
      CompartmentComponent.id
    )?.compartmentSize;

    const suggestions = frontLayoutProducts.filter(p =>
      compartmentSize
        ? p.communicatedDimensions.width === compartmentSize.X &&
          p.communicatedDimensions.depth === compartmentSize.Z &&
          p.communicatedDimensions.height === compartmentSize.Y
        : false
    );
    suggestionsForSelectedFrame = sortProducts(suggestions, [{ path: 'coreProductId' }]);
  }

  const addCompartmentModels = () => {
    const compartmentsInScene = coreApi.entities.getByComponent(CompartmentComponent.id);
    compartmentsInScene.forEach(compartment => {
      compartment.addComponent(new SelectionOverrideComponent());
      compartment.addComponent(new CompartmentModelComponent());
      if (compartmentsInScene.length === 1) {
        // If only one compartment in scene, select it
        compartment.addComponent(new SelectedComponent());
        setSelectedCompartment(compartment);
      }
    });
  };

  const removeCompartmentModels = () => {
    const compartmentsInScene = coreApi.entities.getByComponent(CompartmentComponent.id);
    compartmentsInScene.forEach(compartment => {
      compartment.removeComponent(SelectionOverrideComponent.id);
      compartment.removeComponent(CompartmentModelComponent.id);
    });
  };

  useEffect(() => {
    if (isMobile() && !selected) return;
    addCompartmentModels();
    dispatch(setShowSelectionOverrideIndicators(true));
    return () => {
      removeCompartmentModels();
      dispatch(setShowSelectionOverrideIndicators(false));
      coreApi.entities.unselect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreApi, dispatch, selected]);

  useEffect(() => {
    const onSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedCompartment(entity);
        setSelectedLayout('');
      }
    };
    const onUnSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedCompartment(null);
      }
    };

    entityManager.subscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
    entityManager.subscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    return () => {
      entityManager.unsubscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
      entityManager.unsubscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    };
  }, [coreApi, entityManager]);

  const onLayoutSelected = (layout: Product) =>
    behaviorTracker.custom(() => {
      const frame = selectedCompartment?.getComponent<CompartmentComponent>(CompartmentComponent.id)
        ?.frameEntity;
      if (!selectedCompartment || !frame) return;

      const frontEntitesOnFrame = getFrontsOnFrame(frame);
      const frontEntitesInScene = getFrontsInScene(coreApi);

      const previousFrontDesign =
        frontEntitesOnFrame.length > 0
          ? getMajorityDesign(frontEntitesOnFrame)
          : getMajorityDesign(frontEntitesInScene);

      const placementResult = addFrontLayoutToFrame(
        layout,
        frame,
        products,
        coreApi,
        previousFrontDesign,
        selectedCompartment
      );
      if (placementResult?.placedEntity) {
        setSelectedLayout(layout.coreProductId);
      }
    });

  if (!selectedCompartment) {
    return (
      <SelectInSceneInfo
        message={'storageone.bestaSelectFrameInSceneChatBubbleMessage'}
        image={SelectInSceneImage}
      />
    );
  }
  const props: Props = {
    onLayoutSelected: onLayoutSelected,
    suggestionsForSelectedFrame: suggestionsForSelectedFrame,
    selectedLayout: selectedLayout
  };

  if (isMobile()) {
    return <FrontLayoutsSwiper {...props} />;
  }
  return (
    <List>
      <SingleColumnProductList {...props} />
    </List>
  );
}
